import { useCallback, useMemo, useState } from 'react'

import { CheckpointIProps } from '@/features/floor-plan'

export const useBulkPrint = (checkpoints: CheckpointIProps[]) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const isSelected = useCallback(
    (id: string) => selectedIds.has(id),
    [selectedIds]
  )

  const toggleCheckpoint = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(checkpoints.map((c) => c.id)))
  }, [checkpoints])

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  const selectedCheckpoints = useMemo(
    () => checkpoints.filter((c) => selectedIds.has(c.id)),
    [checkpoints, selectedIds]
  )

  const isAllSelected =
    checkpoints.length > 0 && selectedCheckpoints.length === checkpoints.length
  const isIndeterminate = selectedCheckpoints.length > 0 && !isAllSelected

  const toggleSelectAll = useCallback(() => {
    const areAllSelected =
      checkpoints.length > 0 && checkpoints.every((c) => selectedIds.has(c.id))
    if (areAllSelected) {
      deselectAll()
    } else {
      selectAll()
    }
  }, [checkpoints, selectedIds, selectAll, deselectAll])

  return {
    selectedCheckpoints,
    isSelected,
    toggleCheckpoint,
    toggleSelectAll,
    isAllSelected,
    isIndeterminate,
    selectedCount: selectedCheckpoints.length,
  }
}
